/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import toast from "react-hot-toast"
import { selectAdasProgramsMetaData, selectSensingSystemRequirementsNewReportDialogOpen, selectSensorComponentRequirementsNewReportDialogOpen, selectWorldModelSolutionRequirementsNewReportDialogOpen, setSensingSystemRequirementsNewReportDialogOpen, setSensorComponentRequirementsNewReportDialogOpen, setWorldModelSolutionRequirementsNewReportDialogOpen } from "../../../../../../redux/ducks/AdasPrograms/slice"
import { useAppDispatch, useAppSelector } from "../../../../../../redux/hooks"
import ArgosDialog from "../../../../../components/Dialogs/ArgosDialog"
import { SELECT_DATASET_DIALOG_STYLE } from "../../../../../components/Tree/Parts/JamaTreeContainer"
import { AdasProgramsReportPageIdx, AdasProgramsReportPages } from "../constants"
import { usePostRequest } from "../../../../../../api/helper"
import { CAdasProgramsRequests } from "../../types"
import ADAS_PROGRAMS_CONSTANTS from "../../../constants"
import { StringParam, useQueryParam } from "use-query-params"
import { useMemo } from "react"
import { getPageFromHtabIndex } from "../helpers"
import { ReportWizard } from "./ReportWizard"


export const newReportDialogSlice = {
  [AdasProgramsReportPages.SENSING_SYSTEM_REQUIREMENT]: {
    isDialogOpen: selectSensingSystemRequirementsNewReportDialogOpen,
    setIsDialogOpen: setSensingSystemRequirementsNewReportDialogOpen,
  },
  [AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT]: {
    isDialogOpen: selectSensorComponentRequirementsNewReportDialogOpen,
    setIsDialogOpen: setSensorComponentRequirementsNewReportDialogOpen,
  },
  [AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT]: {
    isDialogOpen: selectWorldModelSolutionRequirementsNewReportDialogOpen,
    setIsDialogOpen: setWorldModelSolutionRequirementsNewReportDialogOpen,
  },
}

export const NewReportDialog = () => {
  const dispatch = useAppDispatch()

  const htabid = ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabIndexUrlKey
  const [htab, setHtab] = useQueryParam(htabid, StringParam)
  const page = useMemo(() => getPageFromHtabIndex(htab as AdasProgramsReportPageIdx), [htab])

  const isOpen = useAppSelector(newReportDialogSlice[page].isDialogOpen)
  const closeDialog = () => {dispatch(newReportDialogSlice[page].setIsDialogOpen(false))}

  const adasProgramMetaData = useAppSelector(selectAdasProgramsMetaData)
  const adasProgramId = adasProgramMetaData?.adasProgramId

  /** Request handles */
  const createReportMetadataHandle = usePostRequest(CAdasProgramsRequests.createCoverageRequirementReportMetadata.url)
  const createReportDataHandle = usePostRequest(CAdasProgramsRequests.createCoverageRequirementReportData.url)
  const createReportErrorMsg = "An error occurred while creating the report"

  interface ReportData {
    id: string
    datasetId: string
    format: string
    name: string
  }

  interface ReportsData {
    [key: string]: {
      datasetId: string
      reportFormat: string
      reportName: string
    }
  }

  /** Create report action */
  const createReport = (selectedReportData: any) => {
    /** Close dialog, table will show loading state */
    closeDialog()

    /** Create report metadata item */
    createReportMetadataHandle({
      adasProgramId,
      page,
      selectedReportData,
    }).then((res) => {
      /** Check if metadata was successfully created */
      const reportData = res.reportData
      if (reportData && reportData.length !== 0) {
        /** Create report data item */
        const reportsData: ReportsData = {}
        for (const row of reportData as ReportData[]) {
          reportsData[row.id] = {
            datasetId: row.datasetId,
            reportFormat: row.format,
            reportName: row.name,
          }
        }
        createReportDataHandle({
          action: 'createReport',
          page,
          adasProgramId,
          selectedReportData,
          reportsData,
        }).then(() => {}).catch((err) => {
          console.log(createReportErrorMsg, err)
          toast.error(createReportErrorMsg)
        })
      }
    }).catch((err) => {
      console.log(createReportErrorMsg, err)
      toast.error(createReportErrorMsg)
    })
  }

  return (
    <ArgosDialog
      {...SELECT_DATASET_DIALOG_STYLE}
      open={isOpen}
      onClose={closeDialog}
      headerTitle="New Report"
    >
      <ReportWizard
        page={page}
        onSave={(reportData: any) => {createReport(reportData)}}
        onClickClose={closeDialog}
        isEdit={false}
      />
    </ArgosDialog>
  )
}
